import type { CSSProperties } from 'react';
import { maxGuessesFor, type EvaluatedGuess } from '@lexicon/core';
import { Tile, type TileVisualState } from './Tile';
import { useStrings } from '../state/AppContext';

export interface BoardProps {
  /**
   * The game's answer. Its length is the board's width and, through
   * `maxGuessesFor`, its height (spec §10.1) — the board never needs to be
   * told either separately, and so can never be told them wrongly.
   */
  answer: string;
  guesses: readonly EvaluatedGuess[];
  draft: string;
  revealingRow: number | null;
  shaking: boolean;
  /** Row that plays the win dance, or null. */
  danceRow: number | null;
}

export function Board({ answer, guesses, draft, revealingRow, shaking, danceRow }: BoardProps) {
  const t = useStrings();
  const activeRow = guesses.length;
  const columns = answer.length;
  const rows = maxGuessesFor(answer);
  // Rows need nothing from CSS — they are flex children and divide the
  // available height themselves. The column count does: it is what caps a
  // tile's height so `columns` squares plus their gaps still fit the
  // board's width (see .board-row in index.css).
  const style = { '--board-cols': columns } as CSSProperties;

  return (
    <div role="group" aria-label={t.header.guessGrid} className="board" style={style}>
      {Array.from({ length: rows }, (_, row) => {
        const guess = guesses[row];
        const isActive = row === activeRow;
        const rowClass = [
          'board-row',
          shaking && isActive ? 'row--shake' : '',
        ].filter(Boolean).join(' ');

        return (
          <div key={row} className={rowClass}>
            {Array.from({ length: columns }, (_, col) => {
              const letter = guess ? guess.word[col]! : (isActive ? draft[col] ?? '' : '');
              const state: TileVisualState = guess
                ? guess.tiles[col]!
                : letter ? 'filled' : 'empty';
              return (
                <div key={col} className="board-tile">
                  <Tile
                    letter={letter}
                    state={state}
                    row={row + 1}
                    col={col + 1}
                    revealing={revealingRow === row}
                    popping={isActive && letter !== '' && col === draft.length - 1}
                    dancing={danceRow === row}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
